import {
    Avatar,
    AvatarImage,
    AvatarFallback,
    Badge,
    Button,
    Card,
    CardAccentPanel,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardImage,
    CardMeta,
    CardTitle,
    Code,
    Heading,
    Text,
    AvatarBadge,
    AvatarGradientFallback,
    Stack,
    HStack,
    Container,
    CardIconSlot,
    ChatItem,
    Mono,
    Link,
    Body,
    Caption,
    Eyebrow,
    Muted,
    StatusBadge,
    type StatusBadgeProps,
} from "@athena/ui";
import { StatCard } from "@athena/ui";
import { toast } from "sonner";
import { BookOpen } from "lucide-react";

type StatusBadgeVariant = NonNullable<StatusBadgeProps["variant"]>;
type StatusBadgeSize = NonNullable<StatusBadgeProps["size"]>;

/** Keyed by variant so a new one fails to compile until it is shown here too. */
const STATUS_BADGE_VARIANTS = Object.keys({
    success: true,
    destructive: true,
    critical: true,
    warning: true,
    muted: true,
    brand: true,
    tag: true,
    pink: true,
    violet: true,
    sky: true,
    amber: true,
    teal: true,
    indigo: true,
    emerald: true,
} satisfies Record<StatusBadgeVariant, true>) as StatusBadgeVariant[];

const STATUS_BADGE_SIZES = Object.keys({ xs: true, sm: true, lg: true, ghost: true } satisfies Record<
    StatusBadgeSize,
    true
>) as StatusBadgeSize[];

export function GalleryPage() {
    return (
        <Container className="p-6">
            <StatCard label="Organizations" value={187} detail="142 universities · 38 Greek · 7 treatment" />
            <StatCard label="Sub-Orgs" value={412} detail="under 64 parent orgs" />
            <StatCard label="Users" value="22,431" detailPrefix="+524" detail="this week" detailColor="green" />
            <StatCard
                label="Seats Used"
                value="22.4k / 28k"
                detailPrefix="3 orgs"
                detail="at cap"
                detailColor="yellow"
            />
            <StatCard label="Churn" value="142" detail="this month" detailColor="red" />
            <div className="flex flex-wrap gap-2">
                <Button variant="default" onClick={() => toast("Event has been created")}>
                    Default
                </Button>
                <Button variant="success" onClick={() => toast.success("Event has been created")}>
                    Success
                </Button>
                <Button variant="warning" onClick={() => toast.info("Be at the area 10 minutes before the event time")}>
                    warning
                </Button>
                <Button
                    variant="destructive"
                    onClick={() => toast.warning("Event start time cannot be earlier than 8am")}
                >
                    destructive
                </Button>
                <Button variant="secondary" onClick={() => toast.error("Event has not been created")}>
                    secondary
                </Button>
                <Button variant="ghost" onClick={() => toast.error("Event has not been created")}>
                    ghost
                </Button>
                <Button variant="outline" onClick={() => toast.error("Event has not been created")}>
                    outline
                </Button>
                <Button variant="link" onClick={() => toast.error("Event has not been created")}>
                    link
                </Button>
            </div>
            <HStack>
                <Avatar active>
                    <AvatarImage src="https://github.com/shadcn.png" />
                    <AvatarFallback>CN</AvatarFallback>
                </Avatar>
                <Avatar>
                    <AvatarImage src="https://github.com/shadcn.png" />
                    <AvatarFallback>CN</AvatarFallback>
                </Avatar>
                <Avatar>
                    <AvatarGradientFallback from="from-[var(--color-secondary)]" to="to-[var(--color-primary)]">
                        KS
                    </AvatarGradientFallback>
                </Avatar>
                <Avatar>
                    <AvatarImage src="https://github.com/shadcn.png" />
                    <AvatarFallback>CN</AvatarFallback>
                    <AvatarBadge></AvatarBadge>
                </Avatar>
            </HStack>
            <HStack>
                <Badge variant={"default"}>Default</Badge>
                <Badge variant={"destructive"}>Destructive</Badge>
                <Badge variant={"secondary"}>Secondary</Badge>
                <Badge variant={"outline"}>Outline</Badge>
            </HStack>
            <Stack>
                {STATUS_BADGE_VARIANTS.map((variant) => (
                    <HStack key={variant} className="flex-wrap">
                        <Mono className="w-28">{variant}</Mono>
                        {STATUS_BADGE_SIZES.map((size) => (
                            <StatusBadge key={size} variant={variant} size={size}>
                                {size}
                            </StatusBadge>
                        ))}
                        {STATUS_BADGE_SIZES.map((size) => (
                            <StatusBadge key={`${size}-dot`} variant={variant} size={size} dot>
                                {size} · dot
                            </StatusBadge>
                        ))}
                    </HStack>
                ))}
            </Stack>
            <Stack>
                <Heading level={1}>Heading 1</Heading>
                <Heading level={2}>Heading 2</Heading>
                <Heading level={3}>Heading 3</Heading>
                <Heading level={4}>Heading 4</Heading>
                <Heading level={5}>Heading 5</Heading>
                <Heading level={6}>Heading 6</Heading>

                <Text size="xl">Text xl</Text>
                <Text size="lg">Text lg</Text>
                <Text size="base">Text base</Text>
                <Text size="sm">Text sm</Text>
                <Text size="xs">Text xs</Text>

                <Text weight="normal">Text normal</Text>
                <Text weight="medium">Text medium</Text>
                <Text weight="semibold">Text semibold</Text>
                <Text weight="bold">Text bold</Text>

                <Text muted>Text muted</Text>

                <Code>Code</Code>
            </Stack>
            <Stack>
                <Button variant="default">Primary</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="outline">Outline</Button>
                <Button variant="destructive">Delete</Button>
                <Button variant="success">Save</Button>
                <Button variant="warning">Caution</Button>
                <Button variant="link">Learn more</Button>

                <Button size="xs">Tiny</Button>
                <Button size="sm">Small</Button>
                <Button size="default">Default</Button>
                <Button size="lg">Large</Button>
                <Button size="xl">Extra Large</Button>

                <Button loading>Saving...</Button>
                <Button loading variant="secondary">
                    Loading
                </Button>

                <Button disabled>Can't click</Button>
            </Stack>
            <Stack>
                <Card variant="default">
                    <CardHeader>
                        <CardTitle>
                            <Eyebrow>Module 02 · Video</Eyebrow>
                            <Heading level={2}>Coping With Triggers</Heading>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <CardDescription>
                            <Body>Recognize what sets you off...</Body>
                            <Muted>Updated 3 days ago · 8 lessons</Muted>
                            <Caption>Visible to Release U learners</Caption>
                            <Link>Open in editor</Link>
                            <Mono size="xs" uppercase badge>
                                v2.4 · 12 min
                            </Mono>
                        </CardDescription>
                    </CardContent>
                    <CardFooter>
                        <Button className="w-full max-w-none">Primary</Button>
                    </CardFooter>
                </Card>
                <Card variant="default">
                    <CardImage className="h-48">
                        <img src={"https://picsum.photos/200/300"} className="h-full w-full object-cover" />
                    </CardImage>
                    <CardHeader>
                        <CardTitle>Card Content</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <CardDescription>Some text...</CardDescription>
                    </CardContent>
                    <CardFooter>
                        <Button className="w-full max-w-none">Primary</Button>
                    </CardFooter>
                </Card>
                <Card variant="elevated">
                    <CardHeader>
                        <CardTitle>Card Content</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <CardDescription>Some text...</CardDescription>
                    </CardContent>
                    <CardFooter>
                        <Button className="w-full max-w-none">Primary</Button>
                    </CardFooter>
                </Card>
                <Card variant="journal">
                    <CardHeader>
                        <CardMeta>WED • DAY 13</CardMeta>
                        <CardIconSlot>
                            <BookOpen size={18} />
                        </CardIconSlot>
                        <CardTitle>Where did you slow down today?</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p>"The walk before coffee. I think I'm starting to like mornings."</p>
                    </CardContent>
                </Card>
                <Card variant="default" hoverable className="flex flex-row">
                    <CardAccentPanel color="primary" />
                    <div className="flex flex-col p-4">
                        <CardTitle>Hacking Your Sleep</CardTitle>
                        <CardDescription>Why: You mentioned trouble sleeping...</CardDescription>
                    </div>
                </Card>
                <Card variant="flat">
                    <CardImage className="h-40 bg-gradient-to-br from-pink-300 to-teal-400">
                        <CardMeta className="absolute bottom-3 left-4">4 MIN</CardMeta>
                    </CardImage>
                    <CardHeader>
                        <CardTitle>The 11am urge</CardTitle>
                    </CardHeader>
                </Card>
                <ChatItem
                    name={"Katie S."}
                    organizationName={"University of Washington"}
                    messagePreview={"I really thought I could do today."}
                    avatarFallback={"KS"}
                    timestamp={"2h"}
                />
                <ChatItem
                    name={"Katie S."}
                    organizationName={"University of Washington"}
                    messagePreview={"I really thought I could do today."}
                    avatarFallback={"KS"}
                    timestamp={"2h"}
                    variant={"urgent"}
                    tag={"After hours"}
                />
                <ChatItem
                    name={"Katie S."}
                    organizationName={"University of Washington"}
                    messagePreview={"I really thought I could do today."}
                    avatarFallback={"KS"}
                    timestamp={"2h"}
                    unreadCount={4}
                />
                <ChatItem
                    name={"Katie S."}
                    organizationName={"University of Washington"}
                    messagePreview={"I really thought I could do today."}
                    avatarFallback={"KS"}
                    timestamp={"2h"}
                    unreadCount={4}
                    variant={"accented"}
                />
            </Stack>
        </Container>
    );
}
